import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridEventListener,
  gridClasses,
  GridRowClassNameParams,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import PageContainer from '../layout/PageContainer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { UnitType, unitTypeToUnit, unitTypeToUnits, type Item } from '../../models/Item';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { InventoryItem } from '../../models/InventoryItem';
import { GET_ITEMS_QUERY } from '../../queries/ItemQueries';
import dayjs from "dayjs";

const INITIAL_PAGE_SIZE = 100;

export default function ItemList() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [getItems, { loading: getItemsLoading, error: getItemsError, data }] = useLazyQuery(
    GET_ITEMS_QUERY);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: searchParams.get('pageSize')
      ? Number(searchParams.get('pageSize'))
      : INITIAL_PAGE_SIZE,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [rowsState, setRowsState] = useState<{
    rows: Item[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const loadData = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {

      getItems();

      if (getItemsError) {
        setError(getItemsError);
      }

      setRowsState({
        rows: data?.items || [],
        rowCount: data?.items.length || 0,
      });
    } catch (listDataError) {
      setError(listDataError as Error);
    }

    setIsLoading(false);
  }, [getItems, getItemsError, data?.items]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = useCallback(() => {
    if (!isLoading) {
      loadData();
    }
  }, [isLoading, loadData]);

  const handleRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/dashboard/items/${row.id}`);
    },
    [navigate],
  );

  const handleCreateClick = useCallback(() => {
    navigate('/dashboard/items/new');
  }, [navigate]);

  const initialState = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        display: 'flex',
        flex: 2,
        valueGetter: (_, row) => `${row.name} (${row.isIngredient ? 'Ingredient' : 'Item'})`
      },
      {
        field: 'description',
        headerName: 'Description',
        display: 'flex',
        flex: 1
      },
      {
        field: 'totalQuantityOnHand',
        headerName: 'Total QOH (lbs)',
        type: 'number',
        display: 'flex',
        flex: 1,
        valueFormatter: (value: number | null) => (value || 0).toFixed(3)
      },
      {
        field: 'inventoryItems',
        headerName: 'Inventory Items',
        sortable: false,
        flex: 5,
        minWidth: 700,
        renderCell: (params) => {
          return (
            (params.row.inventoryItems || []).length === 0 ?
              <span>No Items (Out of Stock)</span> :
              <Table sx={{ minWidth: 700 }} aria-label="dense table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row" align="left">Lot #</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} component="th" scope="row" align="left">Vendor</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} component="th" scope="row" align="right">QOH</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} component="th" scope="row" align="right">Ordered Qty</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} component="th" scope="row" align="right">Ordered Date</TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} component="th" scope="row" align="right">Cost per Unit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(params.row.inventoryItems || []).map((inventoryItem: InventoryItem) => (
                    <TableRow
                      key={inventoryItem.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 }, backgroundColor: inventoryItem.isArchived ? '#fac1c0' : '' }}
                    >
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} align="left">
                        <a href={`/dashboard/inventory-items/${inventoryItem.id}`}>{inventoryItem.lotNumber || 'N/A'}</a>
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} align="left">
                        <a href={`/dashboard/vendors/${inventoryItem.vendor?.id}`}>{inventoryItem.vendor?.name || 'N/A'}</a>
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} align="right">
                        {(inventoryItem.quantityOnHand ?? 0).toFixed(3)} {unitTypeToUnits(params.row.unitType as UnitType)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} align="right">
                        {inventoryItem.quantityOrdered.toFixed(3)} {unitTypeToUnits(params.row.unitType as UnitType)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} align="right">
                        {dayjs(inventoryItem.purchaseOrder?.dateTimePurchased).format('M/D/YYYY')}
                      </TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '16%' }} align="right">
                        {inventoryItem.totalCostPerUnit?.toFixed(3)} $/{unitTypeToUnit(params.row.unitType as UnitType)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          );
        }
      }
    ],
    [],
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    if (params.row.outOfStock) {
      return 'out-of-stock';
    }

    if (params.row.lowStock) {
      return 'low-stock';
    }

    return '';
  }

  const pageTitle = 'Items';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        </Stack>
      }
    >
      <Box sx={{ flex: 1, width: '100%' }}>
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
          <Box sx={{ height: '64vh', width: '100%' }}>
            <DataGrid
              density="compact"
              rows={rowsState.rows}
              columns={columns}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              disableRowSelectionOnClick
              onRowClick={handleRowClick}
              loading={isLoading || getItemsLoading}
              initialState={initialState}
              showToolbar
              pageSizeOptions={[INITIAL_PAGE_SIZE]}
              getRowHeight={() => 'auto'}
              getRowClassName={getRowClassName}
              sx={[
                () => ({
                  '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
                  '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
                  '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
                  [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                    outline: 'transparent',
                  },
                  [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: 'none',
                  },
                  [`& .${gridClasses.row}:hover`]: {
                    cursor: 'pointer',
                  },
                  '.out-of-stock': {
                    backgroundColor: '#fac1c0',
                    '&:hover': {
                      backgroundColor: '#fac1c0',
                      filter: 'brightness(105%)'
                    }
                  },
                  '.low-stock': {
                    backgroundColor: '#f8d7c0',
                    '&:hover': {
                      backgroundColor: '#f8d7c0',
                      filter: 'brightness(105%)'
                    },
                  },
                }),
                (theme) =>
                  theme.applyStyles('dark', {
                    '.out-of-stock': {
                      backgroundColor: '#ed201c',
                      '&:hover': {
                        backgroundColor: '#ed201c',
                        filter: 'brightness(120%)'
                      }
                    },
                    '.low-stock': {
                      backgroundColor: '#e67222',
                      '&:hover': {
                        backgroundColor: '#e67222',
                        filter: 'brightness(120%)'
                      }
                    }
                  })]}
              slotProps={{
                loadingOverlay: {
                  variant: 'circular-progress',
                  noRowsVariant: 'circular-progress',
                },
                baseIconButton: {
                  size: 'small',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </PageContainer>
  );
}